import { ReactNode } from "react";
import { ArrowLeft, Home, ShieldAlert } from "lucide-react";
import { useKeycloak } from "@/providers/KeycloakProvider";

export interface AccessRules {
  usersIncluded?: string[];
  usersExcluded?: string[];
  rolesIncluded?: string[];
  rolesExcluded?: string[];
}

interface AccessGuardProps extends AccessRules {
  children: ReactNode;
}

interface AccessContext {
  userName?: string;
  userRoles?: string[];
}

export const hasAccess = (
  { userName, userRoles = [] }: AccessContext,
  { usersIncluded, usersExcluded, rolesIncluded, rolesExcluded }: AccessRules
) => {
  if (!userName) {
    return false;
  }

  // Exclusions always deny access, regardless of include matches.
  if (usersExcluded && usersExcluded.includes(userName)) {
    return false;
  }

  if (rolesExcluded && rolesExcluded.some((role) => userRoles.includes(role))) {
    return false;
  }

  const hasUserInclude = !!usersIncluded && usersIncluded.includes(userName);
  const hasRoleInclude =
    !!rolesIncluded && rolesIncluded.some((role) => userRoles.includes(role));
  const hasAnyIncludeRule = !!usersIncluded || !!rolesIncluded;

  // If include rules are provided, user OR role match is enough.
  if (hasAnyIncludeRule) {
    return hasUserInclude || hasRoleInclude;
  }

  // No include rules means open access unless explicitly excluded.
  return true;
};

export const useHasAccess = (rules: AccessRules) => {
  const { access, userProfile } = useKeycloak();

  return hasAccess(
    {
      userName: userProfile?.username,
      userRoles: access?.roles ?? [],
    },
    rules
  );
};

export const AccessGuard = ({
  children,
  usersIncluded,
  usersExcluded,
  rolesIncluded,
  rolesExcluded,
}: AccessGuardProps) => {
  const hasUserAccess = useHasAccess({
    usersIncluded,
    usersExcluded,
    rolesIncluded,
    rolesExcluded,
  });

  if (!hasUserAccess) {
    return (
      <div className="min-h-[65vh] w-full flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-xl rounded-2xl border border-destructive/30 bg-destructive/5 p-8 shadow-sm">
          <div className="mx-auto mb-5 flex size-16 items-center justify-center rounded-full bg-destructive/15 text-destructive">
            <ShieldAlert className="size-8" />
          </div>

          <h2 className="text-center text-2xl font-bold text-destructive">
            Access Denied
          </h2>
          <p className="mx-auto mt-2 max-w-md text-center text-sm leading-relaxed text-muted-foreground">
            You do not have permission to view this content. If you believe this
            is a mistake, contact an administrator.
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              <ArrowLeft className="size-4" />
              Go back
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.href = "/";
              }}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              <Home className="size-4" />
              Go to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
